import React, { useState, useEffect, useMemo } from 'react';
import { TableLoader } from '../../../components/Common/Loader';
import { Plus, Search, X, Check, ArrowLeft, Eye, Edit2 } from 'lucide-react';
import { getRoutesAPI, createRouteAPI, updateRouteAPI, getDeposAPI } from '../../../api/locations';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

export interface RouteDepo {
  id: number;
  zoneId?: number;
  name: string;
  sapCode?: string | null;
  sapName?: string | null;
  zone?: {
    id: number;
    name: string;
  };
}

export interface RouteItem {
  id: number;
  depoId?: number;
  name: string;
  sapCode?: string | null;
  status?: number | string;
  createdBy?: number | string | null;
  createdAt?: string;
  updatedAt?: string;
  depo?: RouteDepo;
}

const initialRoutesMock: RouteItem[] = [
  { id: 39, depoId: 4, name: 'VH_Alibaug_39', status: 1, depo: { id: 4, name: 'ANAND AGRO-PACKING STATION VASHI', sapCode: '1512', zone: { id: 1, name: 'WEST' } } },
  { id: 33, depoId: 4, name: 'VH_AndheriEast_33', status: 1, depo: { id: 4, name: 'ANAND AGRO-PACKING STATION VASHI', sapCode: '1512', zone: { id: 1, name: 'WEST' } } },
  { id: 6, depoId: 6, name: 'BANGLORE DEPOT MILK Route 1', status: 1, depo: { id: 6, name: 'BANGLORE DEPOT MILK', sapCode: '1401', zone: { id: 2, name: 'SOUTH' } } },
  { id: 7, depoId: 7, name: 'CHENNAI DEPOT MILK Route 1', status: 1, depo: { id: 7, name: 'CHENNAI DEPOT MILK - Chennai', sapCode: '1402', zone: { id: 2, name: 'SOUTH' } } },
  { id: 2, depoId: 2, name: 'GAUGANGA NAGPUR Route 1', status: 1, depo: { id: 2, name: 'GAUGANGA NAGPUR', sapCode: '1515', zone: { id: 1, name: 'WEST' } } },
];

export const RoutesList: React.FC = () => {
  const [routes, setRoutes] = useState<RouteItem[]>(initialRoutesMock);
  const [depos, setDepos] = useState<RouteDepo[]>([]);
  const [selectedDepoId, setSelectedDepoId] = useState<string | number>('all');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<RouteItem | null>(null);
  const [viewingRoute, setViewingRoute] = useState<RouteItem | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [depoId, setDepoId] = useState<number>(4);

  const fetchDepos = async () => {
    try {
      const res = await getDeposAPI();
      const rawList = res?.data || res?.depos || (Array.isArray(res) ? res : null);
      if (Array.isArray(rawList)) {
        setDepos(rawList);
      }
    } catch (err) {
      console.warn('Failed to load depos list:', err);
    }
  };

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await getRoutesAPI();
      const rawList = res?.data || res?.routes || (Array.isArray(res) ? res : null);
      if (Array.isArray(rawList)) {
        setRoutes(rawList);
      }
    } catch (err) {
      console.warn('Using fallback data for routes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepos();
    fetchRoutes();
  }, []);

  // Consolidate depos from fetched list & routes data
  const availableDepos = useMemo(() => {
    const map = new Map<number, { id: number; name: string; sapCode?: string | null }>();
    depos.forEach((d) => {
      if (d.id && d.name) map.set(d.id, { id: d.id, name: d.name, sapCode: d.sapCode });
    });
    routes.forEach((r) => {
      if (r.depoId && r.depo?.name) {
        if (!map.has(r.depoId)) {
          map.set(r.depoId, { id: r.depoId, name: r.depo.name, sapCode: r.depo.sapCode });
        }
      } else if (r.depo?.id && r.depo?.name) {
        if (!map.has(r.depo.id)) {
          map.set(r.depo.id, { id: r.depo.id, name: r.depo.name, sapCode: r.depo.sapCode });
        }
      }
    });
    return Array.from(map.values());
  }, [depos, routes]);

  const handleOpenAddModal = () => {
    setEditingRoute(null);
    setName('');
    setDepoId(availableDepos[0]?.id || 4);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (route: RouteItem) => {
    setEditingRoute(route);
    setName(route.name);
    setDepoId(route.depoId || route.depo?.id || availableDepos[0]?.id || 4);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (editingRoute) {
        await updateRouteAPI(editingRoute.id, {
          name,
          depoId,
        });
      } else {
        await createRouteAPI({
          name,
          depoId,
        });
      }
      await fetchRoutes();
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error saving route:', err);
      alert(err?.message || 'Failed to save route');
    }
  };

  const filteredRoutes = routes.filter((r) => {
    const matchesDepo =
      selectedDepoId === 'all' ||
      r.depoId === Number(selectedDepoId) ||
      r.depo?.id === Number(selectedDepoId);

    const matchesSearch =
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.depo && r.depo.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (r.depo?.sapCode && r.depo.sapCode.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesDepo && matchesSearch;
  });

  const handleExportCSV = () => {
    const headers = ['ID', 'Route Name', 'Depo Name', 'Depo SAP Code', 'Status'];
    const rows = filteredRoutes.map((r) => [
      r.id,
      r.name,
      r.depo?.name || '',
      r.depo?.sapCode || availableDepos.find((d) => d.id === r.depoId)?.sapCode || '',
      r.status === 1 || r.status === '1' || r.status === 'Active' ? 'Active' : 'Inactive',
    ]);
    exportToCSV('routes_list', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Routes Management
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-md shadow-[#C1272D]/20 flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Add Route</span>
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Controls Bar */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-base font-bold text-slate-800">All Routes</h3>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            {/* Depo Filter Dropdown */}
            <select
              value={selectedDepoId}
              onChange={(e) => setSelectedDepoId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full sm:w-64 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D] font-semibold cursor-pointer"
            >
              <option value="all">All Depos</option>
              {availableDepos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search routes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D]"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-6">ID</th>
                <th className="py-3.5 px-6">Route Name</th>
                <th className="py-3.5 px-6">Depo Name</th>
                <th className="py-3.5 px-6">Depo SAP Code</th>
                <th className="py-3.5 px-6">Zone</th>
                <th className="py-3.5 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {filteredRoutes.map((r) => {
                const depoSapCode = r.depo?.sapCode || availableDepos.find((d) => d.id === r.depoId)?.sapCode || 'N/A';
                return (
                  <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-6 font-medium text-slate-400">{r.id}</td>
                    <td className="py-4 px-6 font-bold text-slate-800">{r.name}</td>
                    <td className="py-4 px-6 font-medium text-slate-700">
                      {r.depo?.name || availableDepos.find((d) => d.id === r.depoId)?.name || (r.depoId ? `Depo ${r.depoId}` : '-')}
                    </td>
                    <td className="py-4 px-6 font-semibold text-slate-600">{depoSapCode}</td>
                    <td className="py-4 px-6 font-semibold text-slate-700">
                      <span className="px-2.5 py-1 bg-amber-100/70 text-amber-800 rounded-md text-[11px] font-bold">
                        {r.depo?.zone?.name || 'WEST'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewingRoute(r)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEditModal(r)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {loading ? (
                <TableLoader colSpan={6} text="Loading..." />
              ) : filteredRoutes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400 font-medium">
                    No routes found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Route Modal */}
      {viewingRoute && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-extrabold text-slate-800">{viewingRoute.name}</h3>
                <p className="text-xs font-mono font-medium text-slate-400">Route ID: {viewingRoute.id}</p>
              </div>
              <button
                onClick={() => setViewingRoute(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Route Name</span>
                <p className="text-sm font-bold text-slate-900">{viewingRoute.name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Depo</span>
                  <p className="text-sm font-bold text-slate-900">
                    {viewingRoute.depo?.name || availableDepos.find((d) => d.id === viewingRoute.depoId)?.name || (viewingRoute.depoId ? `Depo ${viewingRoute.depoId}` : '-')}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Depo SAP Code</span>
                  <p className="text-sm font-bold text-slate-900">
                    {viewingRoute.depo?.sapCode || availableDepos.find((d) => d.id === viewingRoute.depoId)?.sapCode || '—'}
                  </p>
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Zone</span>
                <span className="px-2.5 py-1 bg-amber-100/70 text-amber-800 rounded-md text-[11px] font-bold">
                  {viewingRoute.depo?.zone?.name || 'WEST'}
                </span>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                onClick={() => setViewingRoute(null)}
                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-extrabold text-slate-800">
                {editingRoute ? 'Edit Route' : 'Add New Route'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Route Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. BANGLORE DEPOT MILK Route 1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Depo *</label>
                <select
                  required
                  value={depoId}
                  onChange={(e) => setDepoId(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D] font-medium"
                >
                  {availableDepos.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} {d.sapCode ? `(${d.sapCode})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold rounded-xl flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingRoute ? 'Save Changes' : 'Add Route'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutesList;


